import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { WidgetMode } from '../../shared/abstract-widget.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule, TooltipModule } from '@syncfusion/ej2-angular-popups';
import { AppErrorMessagesComponent } from 'src/app/base/error-messages/app-error-messages.component';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogComponent as SyncfusionDialogComponent } from '@syncfusion/ej2-angular-popups';
import { GridModule, GroupService, GroupSettingsModel } from '@syncfusion/ej2-angular-grids';
import { ClientService } from 'src/app/base/services/client/client.service';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { YesNoGroupButtonComponent } from 'src/app/base/shared/yes-no-group-button/yes-no-group-button.component';
import { WidgetWithGridBaseComponent } from '../../shared/widget-with-grid-base.component';
import { ZoneDeviceType, ZoneClient, PagedResultDTOOfZoneDTO, SortDirection, FilterDTO, FilterOperator, ClientDTO, ZoneDeviceState, ZoneDeviceDTO } from 'src/app/base/models/models.swagger.generated';
import { MultiSelectFilterComponent } from 'src/app/base/shared/multi-select-filter/multi-select-filter.component';
import { IMultiSelectItem } from 'src/app/base/shared/autocomplete/multi-select-item';
import { interval, map, Observable, of, Subscription } from 'rxjs';
import { ClientAutocompleteComponent } from 'src/app/base/shared/client-autocomplete/client-autocomplete.component';
import { MultiSelectFilterType } from 'src/app/base/shared/multi-select-filter/multi-select-filter-type';
import { MessageDirective, MessageSeverity, MessageVariant } from 'src/app/directives/message.directive';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { DeviceStateWidgetSettingsFormType } from './device-state-widget-settings-form-type';
import { DeviceStateWidgetService } from './device-state-widget.service';
import { AuthService } from 'src/app/base/services/auth.service';
import { NgClass } from '@angular/common';

export const ZONE_NAME_COLUMN_NAME = 'zone.name';

@Component({
    selector: 'app-device-state-widget',
    imports: [NgClass, TranslateModule, DialogModule, AppErrorMessagesComponent, ReactiveFormsModule, TooltipModule, GridModule, DropDownListModule, YesNoGroupButtonComponent, MultiSelectFilterComponent, ClientAutocompleteComponent, MessageDirective],
    templateUrl: './device-state-widget.component.html',
    styleUrl: './device-state-widget.component.scss',
    providers: [DeviceStateWidgetService, GroupService]
})
export class DeviceStateWidgetComponent extends WidgetWithGridBaseComponent<DeviceStateWidgetSettingsFormType> implements OnInit, OnDestroy {
  @ViewChild('widgetSettingsDialog')
  public widgetSettingsDialog: SyncfusionDialogComponent;

  @ViewChild('zoneMultiselect')
  public zoneMultiselect: MultiSelectFilterComponent;

  public defaultSizeX = 3;
  public defaultSizeY = 2;

  public panelId: string;

  public groupOptions?: GroupSettingsModel;

  public MultiSelectFilterType = MultiSelectFilterType;
  public typeFilter = GridConstantsFactory.createCheckBoxFilter();
  public stateFilter = GridConstantsFactory.createCheckBoxFilter();

  WidgetMode = WidgetMode;
  MessageVariant = MessageVariant;
  MessageSeverity = MessageSeverity;

  public get title(): string {
    const title = this.editSettingsForm.controls.title.value;
    if (title && title.length > 0) {
      return title;
    }
    return "i18n.domain.home.dashboard.shared.widget-types.DeviceStateWidget";
  }

  public get selectedClientDto(): ClientDTO {
    return this.editSettingsForm.controls.selectedClient.value;
  }

  public set selectedClientDto(client: ClientDTO) {
    this.editSettingsForm.controls.selectedClient.setValue(client);

    if (this.isInitialized) {
      this.editSettingsForm.controls.selectedZoneIds.setValue(null);
      this.zoneMultiselect?.refreshData();
    }
    this.isInitialized = true;
    this.deviceStateWidgetService.clientId = client?.id;
  }

  public get selectedZoneIds(): number[] {
    return this.editSettingsForm.controls.selectedZoneIds.value;
  }

  public set selectedZoneIds(zoneIds: number[]) {
    this.editSettingsForm.controls.selectedZoneIds.setValue(zoneIds);
    this.deviceStateWidgetService.zoneIds = zoneIds;
  }

  public get hasUserClientAssigned(): boolean {
    return this.authService.hasUserClientAssigned()
  }

  public ZONE_NAME_COLUMN_NAME = 'zone.name';

  private isInitialized = false;
  private autoRefreshSubscription: Subscription;

  constructor(private deviceStateWidgetService: DeviceStateWidgetService,
              protected override fb: FormBuilder,
              clientService: ClientService,
              private zoneClient: ZoneClient,
              translateService: TranslateService,
              private spinnerService: SpinnerService,
              private authService: AuthService) {
    super(fb, clientService, translateService, deviceStateWidgetService);

    this.editSettingsForm = this.fb.group<DeviceStateWidgetSettingsFormType>({
      pageSize: this.fb.control<number>(this.defaultPageSize, Validators.required),
      saveGridFilters: this.fb.control<boolean>(true),
      gridFilter: this.fb.control<string>(null),
      title: this.fb.control<string>(null),
      selectedClient: this.fb.control<ClientDTO>(null, Validators.required),
      selectedZoneIds: this.fb.control<number[]>(null, Validators.required)
    });

    if (this.hasUserClientAssigned) {
      this.selectedClientDto = this.authService.getUserInfo().client;
    }
  }

  private onDeleteCallback: (panelId: string) => void;

  public override ngOnInit(): void {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.deviceStateWidgetService, this.spinnerService)
    });

    this.groupOptions = { showDropArea: false, columns: [this.ZONE_NAME_COLUMN_NAME] };

    // Auto-refresh the grid every minute (60000 ms)
    this.autoRefreshSubscription = interval(60000).subscribe(() => {
      this.refreshGridData();
    });

    super.ngOnInit();
  }

  public override ngOnDestroy(): void {
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
    }
    super.ngOnDestroy();
  }

  public initializeParameters(panelId: string, serializedParameters: string, onDeleteCallback: (panelId: string) => void) {
    this.panelId = panelId;
    this.onDeleteCallback = onDeleteCallback;
    this.editSettingsForm.patchValue(JSON.parse(serializedParameters));

    this.pageSettings.pageSize = this.editSettingsForm.controls.pageSize.value;

    //Set properties in service
    const selectedClient = this.editSettingsForm.controls.selectedClient.value;
    const selectedZoneIds = this.editSettingsForm.controls.selectedZoneIds.value;

    if (selectedClient) {
      this.deviceStateWidgetService.clientId = selectedClient.id;
    }

    if (selectedZoneIds) {
      this.deviceStateWidgetService.zoneIds = selectedZoneIds;
    }
  }

  public onDelete() {
    this.onDeleteCallback(this.panelId);
  }

  public onConfigure() {
    this.widgetSettingsDialog.show();
  }

  public onForceRefresh() {
    if (this.editSettingsForm.controls.selectedClient.value !== null) {
      this.grid.refresh();
    }
  }

  public onCloseSettingsDialog() {
    this.widgetSettingsDialog.hide();
  }

  public saveParameters() {
    this.widgetSettingsDialog.hide();
    this.parametersChanged.next({
      panelId: this.panelId,
      serializedParamerters: JSON.stringify(this.editSettingsForm.getRawValue())
    });
  }

  public getDeviceType(type: ZoneDeviceType): string {
    return 'i18n.shared.zone-device-enums.device-type.' + ZoneDeviceType[type];
  }

  public formatType(device: ZoneDeviceDTO): string {
    if (!device || !device.deviceType) {
      return '';
    }
    return this.translateService.instant('i18n.shared.zone-device-enums.device-type.' + ZoneDeviceType[device.deviceType]);
  }

  public getDeviceState(status: ZoneDeviceState): string {
    return 'i18n.shared.zone-device-enums.state.' + ZoneDeviceState[status];
  }

  public formatState(device: ZoneDeviceDTO): string {
    if (!device || !device.state) {
      return '';
    }
    return this.translateService.instant('i18n.shared.zone-device-enums.state.' + ZoneDeviceState[device.state]);
  }

  public getStateClass(state: ZoneDeviceState): string {
    switch (state) {
      case ZoneDeviceState.Present:
        return 'present';
      case ZoneDeviceState.NotPresent:
        return 'not-present';
      case ZoneDeviceState.Defect:
        return 'defect';
      case ZoneDeviceState.Unknown:
      default:
        return 'unknown';
    }
  }

  public searchZoneValues(searchText: string): Observable<IMultiSelectItem[]> {
    var clientId = this.editSettingsForm.controls.selectedClient?.value?.id;
    if (!clientId) {
      return of([]);
    }
    const searchFilters = [
      new FilterDTO({
        isComplex: false,
        field: 'Name',
        operator: FilterOperator.StartsWith,
        value: searchText,
      }),
      new FilterDTO({
        isComplex: false,
        field: 'IsGroupingElement',
        operator: FilterOperator.Equal,
        value: 'false',
      })
    ];
    return this.zoneClient.searchZones(clientId, searchFilters, 0, 0, 'Id', SortDirection.Ascending)
      .pipe(
        map((zoneDTOValues: PagedResultDTOOfZoneDTO) => {
        const data: IMultiSelectItem[] = [];
        zoneDTOValues.items.forEach(zone => {
          data.push({id: zone.id, name: zone.name});
        });
        return data;
      }));
  }

  private refreshGridData(): void {
    if (this.selectedClientDto !== null && this.selectedZoneIds?.length !== 0 && this.grid) {
      this.grid.refresh();
    }
  }
}
